import { appAlert } from '@/lib/appAlert';
import { useState, useMemo, useRef, useEffect } from 'react';
import { Animated, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { DisputeOutcomeCard } from '@/components/DisputeOutcomeCard';
import { ReviewCard } from '@/components/ReviewCard';
import { StarRating } from '@/components/ui/StarRating';
import { ZoomableImageModal } from '@/components/ZoomableImageModal';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { ReviewSummary } from '@/lib/database.types';
import type { DisputeOutcomeEntry } from '@/lib/disputes';
import { tagLabel, POSITIVE_TAGS, NEGATIVE_TAGS, characterizationLabel, type ReviewWithStatus } from '@/lib/reviews';
import { ratingCapFor, safeNum, verificationCapFor, volumeCapFor, reliabilityColorFor, RELIABILITY_GOLD } from '@/lib/format';

function docNumber(id: string): string {
  return `ΧΕΙΡ-${id.slice(0, 8).toUpperCase()}`;
}


type StatFilter = 'completed' | 'cancelled_mutual' | 'cancelled_unilateral' | 'disputes' | null;

type Props = {
  summary: ReviewSummary;
  reviews?: ReviewWithStatus[];
  reviewerNames?: Record<string, string>;
  reviewerNicknames?: Record<string, string | null>;
  reviewerReliability?: Record<string, number | null>;
  phoneVerified?: boolean;
  identityVerified?: boolean;
  disputeOutcomes?: DisputeOutcomeEntry[];
};

function computeRatingDist(reviews: ReviewWithStatus[]): Record<number, number> {
  const dist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const r of reviews) {
    const star = Math.round(r.rating);
    if (star >= 1 && star <= 5) dist[star]++;
  }
  return dist;
}

type TagCount = { tag: string; label: string; count: number; sentiment: 'positive' | 'negative' };

function computeTopTags(reviews: ReviewWithStatus[], topN = 4): { pos: TagCount[]; neg: TagCount[] } {
  const posCounts: Record<string, number> = {};
  // Για τα αρνητικά μετράμε μοναδικούς reviewers (όχι συνολικές εμφανίσεις)
  const negReviewers: Record<string, Set<string>> = {};

  for (const r of reviews) {
    for (const t of r.tags) {
      if (POSITIVE_TAGS.includes(t as any)) {
        posCounts[t] = (posCounts[t] ?? 0) + 1;
      } else if (NEGATIVE_TAGS.includes(t as any)) {
        if (!negReviewers[t]) negReviewers[t] = new Set();
        negReviewers[t].add(r.reviewer_id);
      }
    }
  }

  const pos: TagCount[] = POSITIVE_TAGS
    .filter((t) => posCounts[t])
    .map((t) => ({ tag: t, label: tagLabel(t), count: posCounts[t], sentiment: 'positive' as const }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);

  // Αρνητικά: εμφανίζονται μόνο αν ≥2 διαφορετικοί reviewers το έχουν επιλέξει
  const neg: TagCount[] = Object.entries(negReviewers)
    .filter(([, set]) => set.size >= 2)
    .map(([t, set]) => ({ tag: t, label: tagLabel(t as any), count: set.size, sentiment: 'negative' as const }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);

  return { pos, neg };
}

export function ReviewSummaryCard({ summary, reviews, reviewerNames, reviewerNicknames, reviewerReliability, phoneVerified, identityVerified, disputeOutcomes }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [expanded, setExpanded] = useState(false);
  const [activeFilter, setActiveFilter] = useState<StatFilter>(null);
  const [disputesExpanded, setDisputesExpanded] = useState(false);
  const [viewerUri, setViewerUri] = useState<string | null>(null);

  const hasReviews = safeNum(summary.review_count) > 0;
  const effective = safeNum(summary.effective_rating);
  const raw = safeNum(summary.avg_rating);
  const penalized = hasReviews && effective < raw;
  const canExpand = hasReviews && !!reviews && reviews.length > 0;

  // Οροφές αξιοπιστίας — κοινή πηγή αλήθειας (@/lib/format), χρησιμοποιείται
  // και στην αρχική οθόνη ώστε να μη διαφέρουν τα δύο σημεία.
  const verificationCap = verificationCapFor(identityVerified);
  const ratingCap = ratingCapFor(summary.review_count, summary.effective_rating);
  const volumeCap = volumeCapFor(summary.completed_count);
  const completionRateCap = Math.min(verificationCap, ratingCap, volumeCap);
  const rawRate = summary.completion_rate != null ? summary.completion_rate : null;
  const cappedRate = rawRate != null ? Math.min(rawRate, completionRateCap) : null;
  const isAtCap = rawRate != null && rawRate >= completionRateCap && completionRateCap < 100;
  const bindingCap = Math.min(verificationCap, ratingCap, volumeCap);
  const cappedByRating = isAtCap && ratingCap === bindingCap && ratingCap < verificationCap;
  const cappedByVolume = isAtCap && volumeCap === bindingCap && !cappedByRating;
  const reliabilityColor = reliabilityColorFor(cappedRate, colors.success, colors.textMuted);
  const isTopReliability = cappedRate != null && cappedRate >= 81;
  const glowAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!isTopReliability) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(glowAnim, { toValue: 1, duration: 900, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(glowAnim, { toValue: 0, duration: 900, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [isTopReliability, glowAnim]);
  const capHint = cappedByVolume
    ? t('reviewSummary.capHint_fewAgreements')
    : cappedByRating
    ? t('reviewSummary.capHint_lowRating')
    : isAtCap
    // Η επαλήθευση ταυτότητας φτάνει μόνη της στο 100% (δεν χρειάζεται πια
    // κινητό) — άρα το μόνο hint που έχει νόημα εδώ είναι "επαλήθευσε ταυτότητα".
    ? t('reviewSummary.capHint_id', { target: 100 })
    : null;

  const characterization = characterizationLabel(cappedRate, effective, hasReviews, safeNum(summary.completed_count));

  const ratingDist = reviews && reviews.length > 0 ? computeRatingDist(reviews) : null;
  const maxDist = ratingDist ? Math.max(...Object.values(ratingDist), 1) : 1;

  const topTags = reviews && reviews.length > 0 ? computeTopTags(reviews) : null;
  const hasTopTags = topTags && topTags.pos.length > 0;

  const filteredReviews = reviews
    ? activeFilter
      ? reviews.filter((r) => r.agreement_status === activeFilter)
      : reviews
    : [];

  function toggleFilter(f: StatFilter) {
    if (activeFilter === f) {
      setActiveFilter(null);
    } else {
      setActiveFilter(f);
      setExpanded(true);
    }
  }

  return (
    <View style={styles.card}>
      {/* ── Πρωτεύον: Ποσοστό Αξιοπιστίας ── */}
      <Pressable
        style={styles.headerRow}
        onPress={() => {
          const hasData = summary.completion_rate != null;
          appAlert(
            t('reviewSummary.info_reliability'),
            hasData
              ? t('reviewSummary.reliability_info_body')
              : t('reviewSummary.reliability_info_body_noData'),
            [{ text: t('reviewSummary.ok') }]
          );
        }}
        accessibilityRole="button"
      >
        <View style={styles.reliabilityBlock}>
          <Text style={[styles.reliabilityLabel, { color: reliabilityColor }]}>
            {cappedRate != null ? t('reviewSummary.reliability_known') : t('reviewSummary.reliability_unknown')}
          </Text>
          <View style={styles.reliabilityScoreRow}>
            {cappedRate != null ? (
              isTopReliability ? (
                <Animated.View
                  style={{
                    shadowColor: RELIABILITY_GOLD,
                    shadowOffset: { width: 0, height: 0 },
                    shadowRadius: 8,
                    shadowOpacity: glowAnim.interpolate({ inputRange: [0, 1], outputRange: [0.35, 1] }),
                  }}
                >
                  <Text style={[styles.score, { color: reliabilityColor }]}>
                    {cappedRate.toFixed(0)}%
                  </Text>
                </Animated.View>
              ) : (
                <Text style={[styles.score, { color: reliabilityColor }]}>
                  {cappedRate.toFixed(0)}%
                </Text>
              )
            ) : (
              <Text style={[styles.score, styles.scoreUnknown]}>—</Text>
            )}
            <Ionicons name="shield-checkmark-outline" size={18} color={reliabilityColor} style={{ marginTop: 4 }} />
          </View>
        </View>
        <View style={styles.headerRight}>
          {!!characterization && (
            <View style={styles.characterizationBadge}>
              <Text style={styles.characterizationText}>{characterization}</Text>
            </View>
          )}
          {!!capHint && (
            <Text style={styles.capHint}>🔒 {capHint}</Text>
          )}
          {/* Δευτερεύον: αστέρια */}
          {hasReviews ? (
            <>
              <View style={styles.starsRow}>
                <StarRating value={effective} size={15} />
                <Text style={styles.starsScore}>{effective.toFixed(1)}</Text>
              </View>
              <Text style={styles.count}>
                {t('reviewSummary.uniqueUsers', { count: summary.review_count })}
              </Text>
            </>
          ) : (
            <Text style={styles.count}>{t('reviewSummary.noReviews')}</Text>
          )}
          <Text style={styles.tapHint}>{t('reviewSummary.tapForMore')}</Text>
        </View>
      </Pressable>

      {/* Κατανομή αστεριών */}
      {hasReviews && ratingDist && (
        <View style={styles.distSection}>
          {[5, 4, 3, 2, 1].map((star) => {
            const count = ratingDist[star] ?? 0;
            const pct = count / maxDist;
            return (
              <View key={star} style={styles.distRow}>
                <Text style={styles.distStar}>{star}★</Text>
                <View style={styles.distTrack}>
                  <View
                    style={[
                      styles.distFill,
                      star >= 4 ? styles.distFillGood : star === 3 ? styles.distFillMid : styles.distFillBad,
                      { width: `${Math.round(pct * 100)}%` as any },
                    ]}
                  />
                </View>
                <Text style={styles.distCount}>{count}</Text>
              </View>
            );
          })}
        </View>
      )}

      {/* Top θετικές ετικέτες */}
      {hasReviews && (topTags?.pos.length ?? 0) > 0 && (
        <View style={styles.tagsSection}>
          <View style={styles.tagGroup}>
            {topTags!.pos.map((t) => (
              <View key={t.tag} style={[styles.topTag, styles.topTagPos]}>
                <Text style={[styles.topTagText, styles.topTagTextPos]}>{t.label}</Text>
                {t.count > 1 && <Text style={styles.topTagCount}>×{t.count}</Text>}
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Στατιστικά — πατήσιμα για φιλτράρισμα */}
      <View style={styles.stats}>
        <Stat
          label={t('reviewSummary.stat_completed')}
          value={summary.completed_count}
          icon="checkmark-circle-outline"
          color={colors.success}
          active={activeFilter === 'completed'}
          onPress={() => toggleFilter('completed')}
          canExpand={canExpand}
          info={t('reviewSummary.stat_completed_info')}
          okText={t('reviewSummary.ok')}
          styles={styles}
        />
        <Stat
          label={t('reviewSummary.stat_unilateralCancellations')}
          value={summary.cancelled_unilateral_count}
          subLabel={(() => {
            const uni = safeNum(summary.cancelled_unilateral_count);
            const total = safeNum(summary.completed_count) + safeNum(summary.cancelled_mutual_count) + safeNum(summary.cancelled_unilateral_count);
            if (uni === 0 || total < 5) return undefined;
            const rate = uni / total;
            if (rate >= 0.3) return t('reviewSummary.unilateral_veryFrequent');
            if (rate >= 0.15) return t('reviewSummary.unilateral_frequent');
            if (rate >= 0.05) return t('reviewSummary.unilateral_sometimes');
            return t('reviewSummary.unilateral_rare');
          })()}
          icon="close-circle-outline"
          color={colors.danger}
          active={activeFilter === 'cancelled_unilateral'}
          onPress={() => toggleFilter('cancelled_unilateral')}
          canExpand={canExpand}
          info={t('reviewSummary.stat_unilateral_info')}
          okText={t('reviewSummary.ok')}
          styles={styles}
        />
        <Stat
          label={t('reviewSummary.stat_disputes')}
          value={summary.dispute_count ?? 0}
          subLabel={(() => {
            const disputes = summary.dispute_count ?? 0;
            const total = safeNum(summary.completed_count) + safeNum(summary.cancelled_mutual_count) + safeNum(summary.cancelled_unilateral_count);
            if (disputes === 0 || total < 10) return undefined;
            const rate = disputes / total;
            if (rate >= 0.5) return t('reviewSummary.disputes_everyOther');
            if (rate >= 0.3) return t('reviewSummary.disputes_veryFrequent');
            if (rate >= 0.2) return t('reviewSummary.disputes_quiteFrequent');
            if (rate >= 0.1) return t('reviewSummary.disputes_frequent');
            if (rate >= 0.05) return t('reviewSummary.disputes_sometimes');
            if (rate >= 0.02) return t('reviewSummary.disputes_rare');
            return t('reviewSummary.disputes_minimal');
          })()}
          icon="alert-circle-outline"
          color={colors.warning}
          active={disputesExpanded}
          onPress={() => {
            if (disputeOutcomes && disputeOutcomes.length > 0) {
              setDisputesExpanded((v) => !v);
            } else {
              appAlert(t('reviewSummary.info_disputes'), t('reviewSummary.disputes_info_body'), [{ text: t('reviewSummary.ok') }]);
            }
          }}
          canExpand={!!(disputeOutcomes && disputeOutcomes.length > 0)}
          info={t('reviewSummary.disputes_info_body')}
          okText={t('reviewSummary.ok')}
          styles={styles}
        />
      </View>

      {/* Αναλυτικές αξιολογήσεις */}
      {canExpand && (
        <Pressable
          style={({ pressed }) => [styles.expandBtn, pressed && styles.expandBtnPressed]}
          onPress={() => { setExpanded((v) => !v); if (expanded) setActiveFilter(null); }}
          accessibilityRole="button"
        >
          <Text style={styles.expandLabel}>
            {expanded ? t('reviewSummary.toggle_hide') : t('reviewSummary.toggle_show')}
          </Text>
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={15}
            color={colors.primary}
          />
        </Pressable>
      )}

      {/* Dispute outcomes */}
      {disputesExpanded && disputeOutcomes && disputeOutcomes.length > 0 && (
        <View style={styles.reviewsList}>
          <View style={styles.filterBanner}>
            <Ionicons name="alert-circle-outline" size={13} color={colors.warning} />
            <Text style={[styles.filterBannerText, { color: colors.warning }]}>{t('reviewSummary.disputeResults')}</Text>
            <Pressable onPress={() => setDisputesExpanded(false)} hitSlop={8}>
              <Ionicons name="close-circle" size={16} color={colors.textMuted} />
            </Pressable>
          </View>
          {disputeOutcomes.map((entry) => (
            <DisputeOutcomeCard key={entry.dispute_id} entry={entry} />
          ))}
        </View>
      )}

      {expanded && canExpand && (
        <View style={styles.reviewsList}>
          {activeFilter && (
            <View style={styles.filterBanner}>
              <Ionicons name="filter" size={13} color={colors.primary} />
              <Text style={styles.filterBannerText}>
                {activeFilter === 'completed' && t('reviewSummary.filter_completed')}
                {activeFilter === 'cancelled_mutual' && t('reviewSummary.filter_mutual')}
                {activeFilter === 'cancelled_unilateral' && t('reviewSummary.filter_unilateral')}
              </Text>
              <Pressable onPress={() => setActiveFilter(null)} hitSlop={8}>
                <Ionicons name="close-circle" size={16} color={colors.textMuted} />
              </Pressable>
            </View>
          )}
          {filteredReviews.length > 0 ? (
            filteredReviews.map((r) => (
              <ReviewCard
                key={r.id}
                review={r}
                reviewerName={reviewerNames?.[r.reviewer_id] || t('common.user')}
                reviewerNickname={reviewerNicknames?.[r.reviewer_id]}
                reviewerReliability={reviewerReliability?.[r.reviewer_id] ?? null}
                agreementCode={docNumber(r.agreement_id)}
                disputedLoss={r.disputed_loss}
                onPhotoPress={setViewerUri}
              />
            ))
          ) : (
            <Text style={styles.emptyFilter}>{t('reviewSummary.emptyFilter')}</Text>
          )}
        </View>
      )}
      <ZoomableImageModal uri={viewerUri} onClose={() => setViewerUri(null)} />
    </View>
  );
}

function Stat({ label, value, subLabel, icon, color, active, onPress, canExpand, info, okText, styles }: {
  label: string; value: number; subLabel?: string; icon: string; color: string; active: boolean; onPress: () => void; canExpand: boolean; info: string; okText: string; styles: ReturnType<typeof makeStyles>;
}) {
  return (
    <Pressable
      style={({ pressed }) => [styles.stat, active && styles.statActive, pressed && styles.statPressed]}
      onPress={canExpand ? onPress : () => appAlert(label, info, [{ text: okText }])}
      onLongPress={() => appAlert(label, info, [{ text: okText }])}
      accessibilityRole="button"
    >
      <Ionicons name={icon as any} size={18} color={active ? color : value > 0 ? color : color} />
      <Text style={[styles.statValue, (active || value > 0) && { color }]}>{value}</Text>
      <Text style={[styles.statLabel, active && { color }]}>{label}</Text>
      {!!subLabel && <Text style={[styles.statSubLabel, { color }]}>{subLabel}</Text>}
    </Pressable>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.md,
      ...shadow.card,
    },
    empty: {
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    completionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    completionText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    completionValue: {
      color: colors.success,
      fontWeight: '700',
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    reliabilityBlock: {
      alignItems: 'center',
      gap: 2,
    },
    reliabilityScoreRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    score: {
      color: colors.success,
      fontSize: 36,
      fontWeight: '800',
      lineHeight: 40,
    },
    scoreUnknown: {
      color: colors.textMuted,
    },
    headerRight: {
      flex: 1,
      gap: 4,
    },
    reliabilityLabel: {
      fontSize: fontSize.sm,
      fontWeight: '700',
      textAlign: 'center',
    },
    starsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
    },
    starsScore: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    count: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    tapHint: {
      color: colors.textMuted,
      fontSize: 10,
      opacity: 0.6,
    },
    capHint: {
      color: colors.warning,
      fontSize: 11,
      fontWeight: '600',
    },
    characterizationBadge: {
      alignSelf: 'flex-start',
      backgroundColor: `${colors.primary}14`,
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
      marginTop: 2,
    },
    characterizationText: {
      color: colors.primary,
      fontSize: 12,
      fontWeight: '700',
    },
    penalty: {
      color: colors.warning,
      fontSize: fontSize.sm,
    },
    // Κατανομή αστεριών
    distSection: {
      gap: 5,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.sm,
    },
    distRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    distStar: {
      color: colors.textMuted,
      fontSize: 11,
      fontWeight: '600',
      width: 22,
      textAlign: 'right',
    },
    distTrack: {
      flex: 1,
      height: 6,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      overflow: 'hidden',
    },
    distFill: {
      height: '100%',
      borderRadius: radius.full,
      minWidth: 3,
    },
    distFillGood: { backgroundColor: colors.success },
    distFillMid: { backgroundColor: colors.warning },
    distFillBad: { backgroundColor: colors.danger },
    distCount: {
      color: colors.textMuted,
      fontSize: 11,
      width: 16,
      textAlign: 'right',
    },
    // Top ετικέτες
    tagsSection: {
      gap: spacing.xs,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.sm,
    },
    tagGroup: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
    },
    topTag: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      borderRadius: radius.full,
      borderWidth: 1,
      paddingVertical: 4,
      paddingHorizontal: spacing.sm,
    },
    topTagPos: {
      backgroundColor: 'rgba(52,211,153,0.10)',
      borderColor: colors.success,
    },
    topTagNeg: {
      backgroundColor: 'rgba(248,113,113,0.10)',
      borderColor: colors.danger,
    },
    topTagText: {
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    topTagTextPos: { color: colors.success },
    topTagTextNeg: { color: colors.danger },
    topTagCount: {
      color: colors.success,
      fontSize: 11,
      fontWeight: '700',
      opacity: 0.75,
    },
    topTagCountNeg: {
      color: colors.danger,
      fontSize: 11,
      fontWeight: '700',
      opacity: 0.75,
    },
    // Stats
    stats: {
      flexDirection: 'row',
      gap: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.md,
    },
    stat: {
      flex: 1,
      alignItems: 'center',
      gap: 3,
      borderRadius: radius.md,
      paddingVertical: spacing.xs,
    },
    statActive: {
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.borderStrong,
    },
    statPressed: { opacity: 0.7 },
    statValue: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    statLabel: {
      color: colors.textMuted,
      fontSize: 11,
      textAlign: 'center',
      lineHeight: 15,
    },
    statSubLabel: {
      fontSize: 10,
      textAlign: 'center',
      fontWeight: '600',
      marginTop: 1,
    },
    // Expand button
    expandBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: spacing.xs,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      marginTop: -spacing.xs,
    },
    expandBtnPressed: { opacity: 0.7 },
    expandLabel: {
      color: colors.primary,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    reviewsList: {
      gap: spacing.sm,
    },
    filterBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      paddingVertical: spacing.xs,
      paddingHorizontal: spacing.sm,
      borderWidth: 1,
      borderColor: colors.borderStrong,
    },
    filterBannerText: {
      flex: 1,
      color: colors.primary,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    emptyFilter: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      textAlign: 'center',
      paddingVertical: spacing.md,
    },
  });
}
